import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { MessageSquare, Loader2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth";
import { useMutation } from "@tanstack/react-query";
import { RegisterRequestSchema } from "@/lib/api";
import { HTTPError } from "ky";

export const Route = createFileRoute("/auth/register")({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      redirectTo: (search.redirectTo as string) || "/dashboard",
    };
  },
  component: Register,
});

function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Create register mutation using React Query
  const registerMutation = useMutation({
    mutationFn: register,
    onSuccess: () => {
      // Redirect to onboarding flow upon successful registration
      navigate({ to: "/onboarding" });
    },
    onError: (error: Error) => {
      if (error instanceof HTTPError) {
        // Handle HTTP errors
        if (error.response.status === 409) {
          setError(
            "This email is already registered. Please use a different email or try logging in.",
          );
        } else {
          setError("An error occurred during registration. Please try again.");
        }
      } else {
        setError(error.message || "An unexpected error occurred");
      }
      console.error("Registration error:", error);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!termsAccepted) {
      setError("You must accept the Terms of Service and Privacy Policy");
      return;
    }

    try {
      // Validate form data with Zod schema
      const registerData = RegisterRequestSchema.parse({
        name,
        email,
        password,
      });

      // Execute register mutation
      registerMutation.mutate(registerData);
    } catch (err: any) {
      // Handle validation errors
      if (err.errors) {
        setError(err.errors.map((e: any) => e.message).join(", "));
      } else {
        setError("Please check your input and try again.");
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 relative max-w-lg mx-auto">
      {/* Background elements */}
      <div className="absolute top-[20%] right-[10%] w-64 h-64 bg-blue-300 rounded-full blur-3xl opacity-20 animate-float"></div>
      <div
        className="absolute bottom-[10%] left-[15%] w-72 h-72 bg-purple-300 rounded-full blur-3xl opacity-10 animate-float"
        style={{ animationDelay: "2s" }}
      ></div>

      <div className="w-full max-w-md animate-fade-up">
        <div className="mb-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center shadow-md">
              <MessageSquare className="h-6 w-6 text-primary-foreground" />
            </div>
          </div>
          <h1 className="text-3xl font-bold">
            aiEmailAgent<span className="text-blue-600">.</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            The AI-powered email assistant for sales teams
          </p>
        </div>

        <div className="glass rounded-xl p-8 shadow-lg border">
          <div className="space-y-2 mb-6">
            <h2 className="text-2xl font-bold">Create Account</h2>
            <p className="text-muted-foreground text-sm">
              Register to get started with aiEmailAgent
            </p>
          </div>

          {error && (
            <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive mb-4 animate-scale">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="name"
                className="text-sm font-medium leading-none"
              >
                Full Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                required
                disabled={registerMutation.isPending}
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-sm font-medium leading-none"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                required
                disabled={registerMutation.isPending}
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="password"
                className="text-sm font-medium leading-none"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={registerMutation.isPending}
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div className="space-y-2 mt-2">
              <div className="flex items-center space-x-2">
                <input
                  id="terms"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  required
                  disabled={registerMutation.isPending}
                  className="h-4 w-4 rounded border text-primary focus:ring-primary"
                />
                <label
                  htmlFor="terms"
                  className="text-xs text-muted-foreground"
                >
                  I agree to the{" "}
                  <a
                    href="/terms"
                    className="text-primary hover:underline font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy"
                    className="text-primary hover:underline font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground py-2.5 mt-2 hover:shadow-md transition-all duration-200 rounded-lg text-sm font-medium"
              disabled={registerMutation.isPending}
            >
              {registerMutation.isPending ? (
                <span className="flex items-center">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating account...
                </span>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            Already have an account?{" "}
            <Link
              to="/auth/login"
              className="font-medium text-primary hover:underline"
              search={{ redirectTo: "/dashboard" }}
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
